import { Mutex } from 'async-mutex';
import { z } from 'zod';
import { isUnknownEnumValue, mapFilespaceFormatToVersion, mapFilespaceVersionToFormat, MemberEncryptionType, MemberType, SsoLoginRequirement, WorkspaceLoginMfaRequirement, } from '@lucidlink/dto/validation';
import { CommonMemberCryptoUpdateDto, FilespaceLoginDto, FilespaceProvisionResponseDto, FilespaceResponseDto, GroupCryptoUpdateDto, GuestLinkWorkspaceLoginDto, SsoLoginParamsResponseDto, StorageProvidersResponseDto, TokensResponseDto, WorkspaceGroupPrincipalSchema, WorkspaceLoginDto, WorkspaceLoginResponseDto, WorkspaceLoginResponseDtoOld, WorkspaceLoginStatusResponseDto, WorkspaceMemberPrincipalSchema, WorkspacePrincipalListResponseParserSchema, WorkspaceSsoLoginDto, } from '@lucidlink/dto/web-service-api';
import { AES_ENCRYPTION_ALGO_PARAMS, arrayToAesKey, arrayToBase64, base64ToArray, decryptWithAes, encryptWithAesRaw, exportPrivateCryptoKeyToPem, stringToArray, } from '@lucidlink/ts-crypto/crypto';
import { isNil } from '@lucidlink/tscore';
import { InternalError } from '@lucidlink/web-fs/common';
import { arrayParserFromDto, parserFromDto } from '#fetcher';
import { AccountCrypto } from '#sdk/common';
import { TimeCachedValue } from '#sdk/utils/time-cached-value';
import { isGuestUserAccountDetails } from '#utils/account-details';
import { createBillingManager } from './billing/workspace-billing-manager';
import { AdminCrypto, InvalidCryptoError, MemberCryptoFromAccountCrypto, MemberCryptoFromWorkspaceKey, PrincipalsCrypto, } from './crypto';
import { CryptoMigrator } from './crypto/crypto-migrator';
import { MemberCryptoFromInvitationSecret } from './crypto/member-crypto/member-crypto-from-invitation-key';
import { createDomainManager } from './domain-manager';
import { createFilespaceClient, } from './filespace-client';
import { createGroupManager } from './group-manager';
import { createInvitationManager } from './invitation-manager';
import { createMemberManager } from './member-manager';
import { createMigrationManager } from './migration-manager';
import { prepareFilespaceCreate } from './prepare-filespace-create';
import { createServiceAccountManager } from './service-account-manager';
import { createSsoManager } from './sso-manager';
export const createWorkspaceClient = ({ getSessionManager, getGlobalFetcher, getGlobalTelemetryClient, logger, additionalInfo, getClassicApiContext, workspaceId, }) => {
    return new WorkspaceClientImpl(workspaceId, getGlobalFetcher, getSessionManager, getGlobalTelemetryClient, logger, additionalInfo, getClassicApiContext);
};
export class WorkspaceClientImpl {
    workspaceId;
    getGlobalFetcher;
    getSessionManager;
    getGlobalTelemetryClient;
    logger;
    additionalInfo;
    getClassicApiContext;
    domainManager = undefined;
    ssoManager = undefined;
    invitationManager = undefined;
    serviceAccountManager = undefined;
    billingManager = undefined;
    memberManager = undefined;
    groupManager = undefined;
    migrationManager = undefined;
    linkedFilespaces = new Map();
    guestContext = {};
    filespaceSessionMutexes = new Map();
    workspaceFetcher;
    telemetryClient;
    constructor(workspaceId, getGlobalFetcher, getSessionManager, getGlobalTelemetryClient, logger, additionalInfo, getClassicApiContext) {
        this.workspaceId = workspaceId;
        this.getGlobalFetcher = getGlobalFetcher;
        this.getSessionManager = getSessionManager;
        this.getGlobalTelemetryClient = getGlobalTelemetryClient;
        this.logger = logger;
        this.additionalInfo = additionalInfo;
        this.getClassicApiContext = getClassicApiContext;
    }
    login = async () => {
        await this.loginCache.get();
    };
    guestLogin = async (guestSecret, entityId) => {
        this.guestContext.entityId = entityId;
        this.guestContext.linkSecret = await arrayToAesKey(base64ToArray(guestSecret), {
            ...AES_ENCRYPTION_ALGO_PARAMS,
            extractable: false,
        });
        await this.getSessionManager().storeGuestSecret(this.guestContext.entityId ?? this.workspaceId, this.guestContext.linkSecret);
        await this.login();
    };
    async getSsoLoginParams() {
        return await this.getGlobalFetcher().get(['workspaces', this.workspaceId, 'sso', 'login-params'], {
            parser: parserFromDto(SsoLoginParamsResponseDto),
        });
    }
    ssoLogin = async (accessToken) => {
        await this.loginInternal('workspace-login/sso', {
            body: WorkspaceSsoLoginDto.create({
                workspaceId: this.workspaceId,
                accessToken,
            }),
            parser: parserFromDto(WorkspaceLoginResponseDtoOld),
        });
    };
    async logout() {
        try {
            await this.getSessionManager().workspaceLogout(this.workspaceId, this.guestContext.entityId ?? this.workspaceId);
            const filespaceLogoutPromises = [];
            for (const [filespaceId, _] of this.linkedFilespaces) {
                filespaceLogoutPromises.push(this.filespaceLogout(filespaceId));
            }
            await Promise.all(filespaceLogoutPromises);
        }
        finally {
            this.guestContext = {};
            this.principalsCrypto.peek()?.dispose();
            this.principalsCrypto.reset();
            this.adminCrypto.reset();
            this.memberCrypto.reset();
            this.membersMap.reset();
            this.groupsMap.reset();
            this.serviceAccountsMap.reset();
            this.loginCache.reset();
            this.filespaceSessionMutexes.clear();
        }
    }
    getWorkspaceFetcher() {
        if (!this.workspaceFetcher) {
            this.workspaceFetcher = this.getGlobalFetcher().createSubFetcher({
                path: ['workspaces', this.workspaceId],
                getAccessToken: (forceRefresh) => this.getSessionManager().getFreshWorkspaceToken(this.workspaceId, this.guestContext.entityId ?? this.workspaceId, Boolean(forceRefresh)),
            });
        }
        return this.workspaceFetcher;
    }
    async getLoginStatus() {
        const restoreWorkspaceLoginStatus = await this.getSessionManager().tryRestoreWorkspaceLogin(this.workspaceId, this.guestContext.entityId ?? this.workspaceId);
        if (restoreWorkspaceLoginStatus.isLoggedIn) {
            this.guestContext.linkSecret =
                restoreWorkspaceLoginStatus.guestSecretKey;
            return {
                isLoggedIn: restoreWorkspaceLoginStatus.isLoggedIn,
                ssoLoginRequirement: SsoLoginRequirement.DISABLED,
                mfaRequirement: WorkspaceLoginMfaRequirement.OPTIONAL,
            };
        }
        const workspaceLoginData = await this.getGlobalFetcher().get(['workspace-login', this.workspaceId, 'status'], {
            parser: parserFromDto(WorkspaceLoginStatusResponseDto),
        });
        const accountDetails = await this.getSessionManager().getAccountDetails();
        const isGuest = isGuestUserAccountDetails(accountDetails);
        return {
            isLoggedIn: restoreWorkspaceLoginStatus.isLoggedIn,
            ssoLoginRequirement: workspaceLoginData.ssoLoginRequirement,
            isFullLoginRequired: isGuest,
            mfaRequirement: workspaceLoginData.mfaRequirement,
        };
    }
    async leaveWorkspace() {
        await this.login();
        await this.getWorkspaceFetcher().delete(['members', 'me']);
        return this.logout();
    }
    async getCredentials() {
        await this.login();
        const memberCrypto = await this.memberCrypto.get();
        const memberKeys = await memberCrypto.verifyAndGetCurrentMemberKeys();
        const workspaceId = this.workspaceId;
        const [memberPrivateKeyPem, unencryptedWorkspaceCommonKey, workspaceAccessToken,] = await Promise.all([
            exportPrivateCryptoKeyToPem(memberKeys.encryptPair.privateKey),
            memberCrypto.getWorkspaceCommonKeyArray(),
            this.getSessionManager().getFreshWorkspaceToken(workspaceId, this.guestContext.entityId ?? workspaceId, true),
        ]);
        return {
            memberPrivateKeyPem,
            unencryptedWorkspaceCommonKey,
            workspaceAccessToken,
        };
    }
    // Filespace management
    async createFilespace(params) {
        await this.login();
        const adminCrypto = await this.adminCrypto.get();
        const createResponse = await prepareFilespaceCreate({
            ...params,
            filespaceFormat: await this.resolveFilespaceFormat(params.filespaceFormat, adminCrypto),
        }, adminCrypto);
        return this.getWorkspaceFetcher().post('filespaces', {
            body: createResponse,
            parser: parserFromDto(FilespaceResponseDto),
        });
    }
    async getFilespace(filespaceId) {
        await this.login();
        return this.getWorkspaceFetcher().get(['filespaces', filespaceId], {
            parser: parserFromDto(FilespaceResponseDto),
        });
    }
    async listFilespaces() {
        await this.login();
        const dtos = await this.getWorkspaceFetcher().get('filespaces', {
            parser: arrayParserFromDto(FilespaceResponseDto),
        });
        return dtos;
    }
    listLoggedInFilespaces() {
        return [...this.linkedFilespaces.values()];
    }
    async getFilespaceProvisionStatus() {
        await this.login();
        return this.getWorkspaceFetcher().get(['filespaces', 'provision-status'], {
            parser: parserFromDto(FilespaceProvisionResponseDto),
        });
    }
    async updateFilespace(filespaceId, params) {
        await this.login();
        const dto = await this.getWorkspaceFetcher().patch(['filespaces', filespaceId], {
            body: { ...params },
            parser: parserFromDto(FilespaceResponseDto),
        });
        return dto;
    }
    async sendFilespaceDeleteVerificationCode(filespaceId) {
        await this.login();
        await this.getWorkspaceFetcher().post(['filespaces', filespaceId, 'delete-verification'], {});
    }
    async deleteFilespace(filespaceId, params = {}) {
        await this.login();
        const queryParams = params.code ? { code: params.code } : {};
        await this.getWorkspaceFetcher().delete(['filespaces', filespaceId], {
            queryParams,
        });
    }
    listStorageProviders() {
        return this.getGlobalFetcher().get('config', {
            parser: parserFromDto(StorageProvidersResponseDto),
        });
    }
    async updateMfaRequirement(mfaRequirement) {
        await this.login();
        return this.getWorkspaceFetcher().patch('mfa-requirement', {
            body: { mfaRequirement },
        });
    }
    invitationManagement = () => {
        if (!this.invitationManager) {
            this.invitationManager = createInvitationManager({
                workspaceFetcher: this.getWorkspaceFetcher(),
                workspaceId: this.workspaceId,
                membersMap: this.membersMap,
                getAdminCrypto: this.adminCrypto.get,
                getPrincipalsCrypto: this.principalsCrypto.get,
                loginToWorkspace: this.login,
            });
        }
        return this.invitationManager;
    };
    serviceAccountManagement = () => {
        if (!this.serviceAccountManager) {
            this.serviceAccountManager = createServiceAccountManager({
                workspaceFetcher: this.getWorkspaceFetcher(),
                workspaceId: this.workspaceId,
                serviceAccountsMap: this.serviceAccountsMap,
                getAdminCrypto: this.adminCrypto.get,
                getMemberCrypto: this.memberCrypto.get,
                getPrincipalsCrypto: this.principalsCrypto.get,
                loginToWorkspace: this.login,
            });
        }
        return this.serviceAccountManager;
    };
    domain() {
        if (!this.domainManager) {
            this.domainManager = createDomainManager({
                workspaceFetcher: this.getWorkspaceFetcher(),
                loginToWorkspace: this.login,
                getAdminCrypto: this.adminCrypto.get,
            });
        }
        return this.domainManager;
    }
    sso() {
        if (isNil(this.ssoManager)) {
            this.ssoManager = createSsoManager({
                workspaceFetcher: this.getWorkspaceFetcher(),
                getAdminCrypto: this.adminCrypto.get,
                getInvitationManager: this.invitationManagement,
                loginToWorkspace: this.login,
            });
        }
        return this.ssoManager;
    }
    billing() {
        if (isNil(this.billingManager)) {
            this.billingManager = createBillingManager({
                workspaceFetcher: this.getWorkspaceFetcher(),
                assertIsAdmin: this.assertIsAdmin,
                loginToWorkspace: this.login,
            });
        }
        return this.billingManager;
    }
    memberManagement() {
        if (isNil(this.memberManager)) {
            this.memberManager = createMemberManager({
                workspaceFetcher: this.getWorkspaceFetcher(),
                membersMap: this.membersMap,
                getAdminCrypto: this.adminCrypto.get,
                getPrincipalsCrypto: this.principalsCrypto.get,
                loginToWorkspace: async () => {
                    await this.login();
                },
            });
        }
        return this.memberManager;
    }
    groupManagement() {
        if (!this.groupManager) {
            this.groupManager = createGroupManager({
                workspaceId: this.workspaceId,
                workspaceFetcher: this.getWorkspaceFetcher(),
                groupsMap: this.groupsMap,
                getAdminCrypto: this.adminCrypto.get,
                getPrincipalsCrypto: this.principalsCrypto.get,
                loginToWorkspace: async () => {
                    await this.login();
                },
            });
        }
        return this.groupManager;
    }
    migrationManagement() {
        if (!this.migrationManager) {
            this.migrationManager = createMigrationManager({
                workspaceId: this.workspaceId,
                workspaceFetcher: this.getWorkspaceFetcher(),
                getGlobalFetcher: this.getGlobalFetcher,
                getAdminCrypto: this.adminCrypto.get,
                loginToWorkspace: async () => {
                    await this.login();
                },
                getClassicApiContext: this.getClassicApiContext,
            });
        }
        return this.migrationManager;
    }
    acquireFilespaceSessionMutex(filespaceId) {
        const mutex = this.filespaceSessionMutexes.get(filespaceId) ?? new Mutex();
        this.filespaceSessionMutexes.set(filespaceId, mutex);
        return mutex;
    }
    filespaceLogin(filespaceId, flags) {
        const mutex = this.acquireFilespaceSessionMutex(filespaceId);
        return mutex.runExclusive(() => this.performFilespaceLogin(filespaceId, flags));
    }
    filespaceLogout(filespaceId) {
        const mutex = this.acquireFilespaceSessionMutex(filespaceId);
        return mutex.runExclusive(() => this.performFilespaceLogout(filespaceId));
    }
    async performFilespaceLogin(filespaceId, flags) {
        await this.login();
        const sessionManager = this.getSessionManager();
        const existingFilespaceClient = this.linkedFilespaces.get(filespaceId);
        const isLoggedIn = sessionManager.isLoggedInToFilespace(this.guestContext.entityId ?? filespaceId);
        if (existingFilespaceClient && isLoggedIn) {
            return existingFilespaceClient;
        }
        await this.performFilespaceLogout(filespaceId);
        const filespace = await this.getFilespace(filespaceId);
        const filespaceTokensPromise = this.getWorkspaceFetcher().post('filespace-login', {
            body: FilespaceLoginDto.create({
                filespaceId: filespace.id,
            }),
            parser: parserFromDto(TokensResponseDto),
        });
        this.prefetchForFilespaceClient();
        const filespaceTokens = await filespaceTokensPromise;
        await sessionManager.filespaceLogin(filespace, filespaceTokens, this.guestContext.entityId);
        const newFilespaceClient = await this.createFilespaceClient(filespace, flags);
        await newFilespaceClient.link();
        this.linkedFilespaces.set(filespace.id, newFilespaceClient);
        return newFilespaceClient;
    }
    async performFilespaceLogout(filespaceId) {
        const filespaceClient = this.linkedFilespaces.get(filespaceId);
        if (filespaceClient) {
            await filespaceClient.unlink();
            this.linkedFilespaces.delete(filespaceId);
        }
        await this.getSessionManager().filespaceLogout(this.guestContext.entityId ?? filespaceId);
    }
    async encryptStringWithWorkspaceCommonKey(value, { urlSafe = false } = {}) {
        const memberCrypto = await this.memberCrypto.get();
        const workspaceCommonKey = await memberCrypto.getWorkspaceCommonKey();
        const encrypted = await encryptWithAesRaw(stringToArray(value), workspaceCommonKey);
        return arrayToBase64(encrypted, { urlSafe });
    }
    async decryptStringWithWorkspaceCommonKey(value) {
        const memberCrypto = await this.memberCrypto.get();
        const workspaceCommonKey = await memberCrypto.getWorkspaceCommonKey();
        const { buffer } = await decryptWithAes(value, workspaceCommonKey);
        return new TextDecoder().decode(buffer);
    }
    // NOTE: This is not used outside of a test file
    logWorkspaceEvent(event) {
        this.getTelemetryClient().sendEvent(event);
    }
    async createFilespaceClient(filespace, flags) {
        const accountDetails = await this.getSessionManager().getAccountDetails();
        const memberCrypto = await this.memberCrypto.get();
        const getFreshToken = (forceRefresh) => this.getSessionManager().getFreshFilespaceToken(filespace.workspaceId, filespace.id, Boolean(forceRefresh), this.guestContext.entityId);
        const filespaceFetcher = this.getWorkspaceFetcher().createSubFetcher({
            path: ['filespaces', filespace.id],
            getAccessToken: getFreshToken,
        });
        const filespaceTelemetryFetcher = filespaceFetcher.createSubFetcher({
            stripSufixes: true,
        });
        const filespaceTelemetryClient = this.getTelemetryClient().createSubClient({
            getFetcher: () => filespaceTelemetryFetcher,
            commonData: { filespaceId: filespace.id },
        });
        if (filespace.hubWebSocketEndpoints.length === 0) {
            throw new InternalError('Missing websocket endpoints!');
        }
        const hubEndpoint = filespace.hubWebSocketEndpoints[0];
        const principalName = accountDetails.type === MemberType.USER
            ? accountDetails.email
            : accountDetails.name;
        const filespaceClient = createFilespaceClient({
            logger: this.logger,
            workspaceId: filespace.workspaceId,
            filespaceId: filespace.id,
            legacyFilespaceName: filespace.legacyFilespaceName,
            principalId: memberCrypto.member.id,
            principalName,
            memberCrypto: this.memberCrypto,
            adminCrypto: this.adminCrypto,
            principalsCrypto: this.principalsCrypto,
            membersMap: this.membersMap,
            groupsMap: this.groupsMap,
            serviceAccountsMap: this.serviceAccountsMap,
            hubEndpoint,
            getTelemetryClient: () => filespaceTelemetryClient,
            getFreshToken,
            filespaceFetcher,
            additionalInfo: this.additionalInfo,
            onFilespaceLogout: this.filespaceLogout.bind(this, filespace.id),
            ...(flags && { loginFlags: flags }),
        });
        return filespaceClient;
    }
    getTelemetryClient() {
        if (!this.telemetryClient) {
            this.telemetryClient =
                this.getGlobalTelemetryClient().createSubClient({
                    getFetcher: () => this.getWorkspaceFetcher().createSubFetcher({
                        stripSufixes: true,
                    }),
                    commonData: { workspaceId: this.workspaceId },
                });
        }
        return this.telemetryClient;
    }
    loginInternal = async (loginPath, params) => {
        const restoreWorkspaceLoginStatus = await this.getSessionManager().tryRestoreWorkspaceLogin(this.workspaceId, this.guestContext.entityId ?? this.workspaceId);
        if (restoreWorkspaceLoginStatus.isLoggedIn) {
            this.guestContext.linkSecret =
                restoreWorkspaceLoginStatus.guestSecretKey;
            return;
        }
        const workspaceLoginResponse = await this.getGlobalFetcher().post(loginPath, params);
        await this.getSessionManager().workspaceLogin(this.workspaceId, this.guestContext.entityId ?? this.workspaceId, workspaceLoginResponse);
    };
    loginCache = new TimeCachedValue(async () => {
        if (!this.guestContext.linkSecret) {
            return this.loginInternal('workspace-login', {
                body: WorkspaceLoginDto.create({
                    workspaceId: this.workspaceId,
                }),
                parser: parserFromDto(WorkspaceLoginResponseDtoOld),
            });
        }
        if (!this.guestContext.entityId) {
            return this.loginInternal('workspace-login/guest', {
                body: WorkspaceLoginDto.create({
                    workspaceId: this.workspaceId,
                }),
                parser: parserFromDto(WorkspaceLoginResponseDtoOld),
            });
        }
        return this.loginInternal('workspace-login/guest-link', {
            body: GuestLinkWorkspaceLoginDto.create({
                workspaceId: this.workspaceId,
                linkId: this.guestContext.entityId,
            }),
            parser: parserFromDto(WorkspaceLoginResponseDto),
        });
    }, {
        expirationTime: 'neverExpire',
    });
    memberCrypto = new TimeCachedValue(async () => {
        const member = await this.memberManagement().getCurrentMember();
        if (member.encryptionType ===
            MemberEncryptionType.NON_ZERO_KNOWLEDGE) {
            if (!member.workspaceKey) {
                throw new InvalidCryptoError('Non-zero-knowledge member is missing workspace key');
            }
            const workspaceKey = await arrayToAesKey(base64ToArray(member.workspaceKey));
            return new MemberCryptoFromWorkspaceKey(member, workspaceKey, this.logger);
        }
        const accountDetails = await this.getSessionManager().getAccountDetails();
        const isUserWithoutCrypto = isGuestUserAccountDetails(accountDetails);
        if (isUserWithoutCrypto || member.type === MemberType.GUEST_LINK) {
            return this.getMemberCryptoFromGuestLinkSecret(member);
        }
        else {
            const accountCrypto = new AccountCrypto(accountDetails);
            return new MemberCryptoFromAccountCrypto(member, accountCrypto, this.logger);
        }
    }, { expirationTime: 'neverExpire' });
    getMemberCryptoFromGuestLinkSecret(currMember) {
        if (!this.guestContext.linkSecret) {
            throw new InvalidCryptoError('Cannot login as guest without link secret');
        }
        return new MemberCryptoFromInvitationSecret(currMember, this.guestContext.linkSecret, this.logger);
    }
    adminCrypto = new TimeCachedValue(async () => {
        const adminCrypto = new AdminCrypto(new TimeCachedValue(() => this.memberManagement().getAdminMember()), await this.memberCrypto.get(), this.logger);
        const migrator = new CryptoMigrator(adminCrypto, this.getWorkspaceFetcher());
        await migrator.migrate();
        return adminCrypto;
    }, { expirationTime: 'neverExpire' });
    assertIsAdmin = async () => {
        await this.adminCrypto.get();
    };
    principalsCrypto = new TimeCachedValue(async () => new PrincipalsCrypto(this.workspaceId, await this.adminCrypto.get(), (principalIds) => this.getWorkspaceFetcher().get('principals', {
        queryParams: { ids: principalIds.join(',') },
        parser: (json) => WorkspacePrincipalListResponseParserSchema.parse(json),
    }), (memberCryptos) => this.getWorkspaceFetcher().patch(`principals/members`, {
        body: CommonMemberCryptoUpdateDto.create(memberCryptos),
        parser: (json) => z.array(WorkspaceMemberPrincipalSchema).parse(json),
    }), (groupCryptos) => this.getWorkspaceFetcher().patch(`principals/groups`, {
        body: GroupCryptoUpdateDto.create(groupCryptos),
        parser: (json) => z.array(WorkspaceGroupPrincipalSchema).parse(json),
    }), this.logger), { expirationTime: 'neverExpire' });
    membersMap = new TimeCachedValue(async () => {
        const members = await this.memberManagement().listMembers();
        const membersMap = new Map();
        for (const member of members) {
            membersMap.set(member.id, member);
        }
        return membersMap;
    });
    groupsMap = new TimeCachedValue(async () => {
        const groups = await this.groupManagement().listGroups();
        const groupsMap = new Map();
        for (const group of groups) {
            groupsMap.set(group.id, group);
        }
        return groupsMap;
    });
    serviceAccountsMap = new TimeCachedValue(async () => {
        const serviceAccountsData = await this.serviceAccountManagement().listServiceAccounts({
            includeCollaborators: true,
        });
        const serviceAccountsMap = new Map();
        for (const serviceAccount of serviceAccountsData.items) {
            serviceAccountsMap.set(serviceAccount.id, serviceAccount);
        }
        return serviceAccountsMap;
    });
    prefetchForFilespaceClient() {
        this.memberCrypto.prefetch();
        this.adminCrypto.prefetch();
        this.principalsCrypto.prefetch();
        this.membersMap.prefetch();
        this.groupsMap.prefetch();
        this.serviceAccountsMap.prefetch();
    }
    async resolveFilespaceFormat(filespaceFormat, adminCrypto) {
        const filespaceVersion = mapFilespaceFormatToVersion(filespaceFormat);
        const { workspaceConfig } = await adminCrypto.adminMember.get();
        const minFilespaceVersion = workspaceConfig?.minFilespaceVersion;
        if (isNil(minFilespaceVersion) ||
            isUnknownEnumValue(minFilespaceVersion)) {
            return filespaceFormat;
        }
        return mapFilespaceVersionToFormat(this.pickMaxFilespaceVersion(filespaceVersion, minFilespaceVersion));
    }
    pickMaxFilespaceVersion(lhs, rhs) {
        return lhs > rhs ? lhs : rhs;
    }
}
